import React, { useEffect, useState } from 'react';
import SectionHeader from './SectionHeader';
import NotesInput from './NotesInput';
import CurrencyInput from './CurrencyInput';

const INCOME_TYPES = [
  'Salary',
  'Social Security',
  'Pension',
  'Annuity',
  'Rental',
  'K-1',
  'Alimony',
  'Child Support',
  'Other',
];

const EMPTY_SOURCE = {
  type: '',
  description: '',
  amount: null,
  frequency: 'yearly',
  is_net: false,
};

/**
 * Section 6 — Income &amp; Recurring Sources.
 *
 * Lists recurring income such as salary, Social Security, pension,
 * annuity, K-1 distributions, rental income, alimony, or child support.
 *
 * Auto-grows like the accounts and debts sections.
 * Pre-populates Salary rows from Career & Income job changes on first visit.
 */
export default function StepIncome({ data, onChange, careerIncomeData }) {
  const [hasMerged, setHasMerged] = useState(false);

  // On first visit to this step, seed salary entries from job_changes
  useEffect(() => {
    if (hasMerged) return;
    setHasMerged(true);

    const existingSources = data?.sources || [];
    const hasAnyData = existingSources.some(
      (s) => s.type || s.description || s.amount,
    );
    if (hasAnyData) return; // user already entered data — don't overwrite

    const jobChanges = careerIncomeData?.job_changes || [];
    const salaryRows = jobChanges
      .filter((j) => j.annual_salary)
      .map((j) => ({
        ...EMPTY_SOURCE,
        type: 'Salary',
        description: [j.person, j.title].filter(Boolean).join(' — '),
        amount: j.annual_salary,
      }));

    if (salaryRows.length > 0) {
      onChange({ ...(data || {}), sources: salaryRows });
    }
  }, [hasMerged, data, careerIncomeData, onChange]);

  const value = data || {};
  const baseSources = (value.sources && value.sources.length > 0)
    ? value.sources
    : [{ ...EMPTY_SOURCE }];

  const lastHasData = !!(
    baseSources[baseSources.length - 1]?.type
    || baseSources[baseSources.length - 1]?.description
    || baseSources[baseSources.length - 1]?.amount
  );

  const sources = lastHasData && baseSources.length < 15
    ? [...baseSources, { ...EMPTY_SOURCE }]
    : baseSources;

  function updateSource(idx, field, val) {
    const next = sources.map((s, i) => (i === idx ? { ...s, [field]: val } : s));
    onChange({ ...value, sources: next });
  }

  function removeSource(idx) {
    const next = sources.filter((_, i) => i !== idx);
    onChange({ ...value, sources: next.length > 0 ? next : [{ ...EMPTY_SOURCE }] });
  }

  // Annualised total (CurrencyInput stores amount as annual already).
  const total = sources.reduce((sum, s) => sum + (Number(s.amount) || 0), 0);

  return (
    <div>
      <SectionHeader
        number={6}
        title="Income &amp; Recurring Sources"
        tip="List recurring income sources such as salary, Social
          Security, pension, annuity, K-1 or rental income, alimony, or
          child support. Use last year's tax return to find annual gross
          figures (before taxes and deductions). Don't list one-time or
          unguaranteed amounts, interest, dividends, or RMDs."
        showAnnualNote={false}
      />

      <p className="small text-muted mb-3">
        You can enter either monthly or annual amounts. Use the <strong>/mo</strong> or <strong>/yr</strong> button
        at the end of the entry box to choose the correct format.
      </p>

      {sources.map((src, idx) => (
        <div key={idx} className="cashflow-section-card">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h6 className="cashflow-subsection-title mb-0">
              Source #{idx + 1}
            </h6>
            {sources.length > 1 && (
              <button
                type="button"
                className="btn btn-link btn-sm text-muted p-0"
                onClick={() => removeSource(idx)}
                title="Remove this income source"
              >
                <i className="bi bi-x-lg" />
              </button>
            )}
          </div>
          <div className="row g-2">
            <div className="col-md-4">
              <label className="form-label">
                Type{idx === 0 && <span className="text-danger ms-1">*</span>}
              </label>
              <select
                className="form-select"
                value={src.type || ''}
                onChange={(e) => updateSource(idx, 'type', e.target.value)}
                required={idx === 0}
              >
                <option value="">Select…</option>
                {INCOME_TYPES.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            <div className="col-md-4">
              <label className="form-label">Description</label>
              <input
                type="text"
                className="form-control"
                placeholder="e.g. Jane's wages, Joe's SS"
                value={src.description || ''}
                onChange={(e) => updateSource(idx, 'description', e.target.value)}
              />
            </div>
            <div className="col-md-4">
              <CurrencyInput
                label={idx === 0 ? 'Amount *' : 'Amount'}
                id={`income_amount_${idx}`}
                value={src.amount}
                onChange={(v) => updateSource(idx, 'amount', v)}
              />
            </div>
            {src.type === 'Social Security' && (
              <div className="col-12">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id={`ss_is_net_${idx}`}
                    checked={!!src.is_net}
                    onChange={(e) => updateSource(idx, 'is_net', e.target.checked)}
                  />
                  <label className="form-check-label small" htmlFor={`ss_is_net_${idx}`}>
                    This is the <strong>net</strong> amount (what I actually receive after Medicare deductions)
                  </label>
                </div>
              </div>
            )}
          </div>
        </div>
      ))}

      {total > 0 && (
        <div className="cashflow-total-box mt-3">
          <span className="fw-semibold">Total Annualized Income</span>
          <span className="cashflow-total-amount">
            ${Math.round(total).toLocaleString()}
          </span>
        </div>
      )}

      <NotesInput
        value={value.notes}
        onChange={(v) => onChange({ ...value, notes: v })}
      />
    </div>
  );
}
